import { useEffect, useState } from 'react'
import { Alert, Button, Checkbox, Form, Input, Modal } from 'antd'
import { LockOutlined } from '@ant-design/icons'
import { MAX_VIEW_NAME_LENGTH } from '../viewer/savedViews'

type Props = {
  open: boolean
  saving: boolean
  /** One line per saved setting, from describeViewState. */
  summaryLines: string[]
  /** The user's current default, if any; ticking "Make this my default" replaces it. */
  currentDefaultName: string | null
  hasPendingFilterChanges: boolean
  onApplyFilters: () => void
  onClose: () => void
  /** Rejects with the API message (e.g. a duplicate name), which is shown under the name field. */
  onSave: (values: { name: string; isDefault: boolean }) => Promise<void>
}

export function SaveViewModal({
  open,
  saving,
  summaryLines,
  currentDefaultName,
  hasPendingFilterChanges,
  onApplyFilters,
  onClose,
  onSave,
}: Props) {
  const [form] = Form.useForm<{ name: string; isDefault: boolean }>()
  const [nameError, setNameError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    form.resetFields()
    setNameError(null)
  }, [form, open])

  function close() {
    if (saving) return
    onClose()
  }

  async function handleSave() {
    const values = await form.validateFields().catch(() => null)
    if (!values) return

    setNameError(null)
    try {
      await onSave({ name: values.name.trim(), isDefault: Boolean(values.isDefault) })
    } catch (error) {
      setNameError(error instanceof Error && error.message ? error.message : "Couldn't save the view. Try again.")
    }
  }

  return (
    <Modal
      title="Save current view"
      open={open}
      onCancel={close}
      closable={!saving}
      maskClosable={!saving}
      keyboard={!saving}
      footer={[
        <Button key="cancel" onClick={close} disabled={saving}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={() => void handleSave()}>
          Save view
        </Button>,
      ]}
    >
      {hasPendingFilterChanges ? (
        <Alert
          type="warning"
          showIcon
          className="!mb-4"
          message="Apply your filter changes first, or they won't be saved."
          action={<Button size="small" onClick={onApplyFilters}>Apply filters</Button>}
        />
      ) : null}

      <Form form={form} layout="vertical" initialValues={{ name: '', isDefault: false }} requiredMark={false}>
        <Form.Item
          name="name"
          label="View name"
          help={nameError ?? undefined}
          validateStatus={nameError ? 'error' : undefined}
          extra={nameError ? undefined : 'Up to 80 characters. Must be different from your other views.'}
          rules={[{ required: true, whitespace: true, message: 'Enter a name for this view.' }]}
        >
          <Input
            maxLength={MAX_VIEW_NAME_LENGTH}
            showCount
            autoFocus
            placeholder="e.g. My WIP review"
            onChange={() => setNameError(null)}
            onPressEnter={() => void handleSave()}
          />
        </Form.Item>

        <div className="mb-4 rounded-md bg-gray-50 px-3 py-2 text-[12px] text-gray-600">
          <div className="mb-1 font-semibold text-gray-700">This view saves</div>
          {summaryLines.length > 0
            ? summaryLines.map((line) => <div key={line}>{line}</div>)
            : <div>The dashboard's own filters and sorting.</div>}
        </div>

        <Form.Item name="isDefault" valuePropName="checked" className="!mb-3">
          <Checkbox>
            <span className="flex flex-col leading-tight">
              <span>Make this my default</span>
              <span className="text-[12px] text-gray-500">
                Opens automatically on this dashboard.
                {currentDefaultName ? ` Replaces "${currentDefaultName}" as default.` : ''}
              </span>
            </span>
          </Checkbox>
        </Form.Item>
      </Form>

      <div className="flex items-center gap-2 text-[12px] text-gray-500">
        <LockOutlined />
        Only you can see this view. It doesn't change the dashboard for anyone else.
      </div>
    </Modal>
  )
}
